import { z } from "zod";
import { signalDirection } from "./parser.js";

/**
 * What the decider is allowed to say about a freshly parsed `new_signal`.
 *
 * Scope is deliberately narrow: whether and how to open the signal the model was given.
 * Follow-ups ("close it", "SL to BE") are resolved deterministically by the M5 ladder
 * before this ever runs — routing an already-resolved follow-up through an LLM would add
 * latency and a fabrication surface for no benefit, and the ladder's whole design point
 * was to make the model the exception path, not the default.
 *
 * `z.union`, not `z.discriminatedUnion` — the parser schema hit this first: OpenAI's
 * structured-output mode rejects the `oneOf` a discriminated union compiles to.
 */
export const deciderAction = z.union([
  z.object({
    type: z.literal("open"),
    /**
     * The direction to actually trade. Equal to the signal's stated direction unless the
     * decider is correcting it against the levels' own geometry — that correction is a
     * decision, never done silently in code, which is why it lives in the model's output
     * rather than being auto-applied by a guard.
     */
    direction: signalDirection,
    directionCorrected: z.boolean(),
  }),
  z.object({
    type: z.literal("skip"),
    reason: z.string().min(1).max(300),
  }),
  z.object({
    type: z.literal("escalate"),
    question: z.string().min(5).max(300),
  }),
]);
export type DeciderAction = z.infer<typeof deciderAction>;

export const deciderOutput = z.object({
  reasoning: z.string().max(800),
  confidence: z.number().min(0).max(1),
  action: deciderAction,
});
export type DeciderOutput = z.infer<typeof deciderOutput>;

/** Bump when the prompt changes so an old decision can be re-run and compared. */
export const DECIDER_PROMPT_VERSION = "2026-08-18.1";
